import React, { useEffect, useMemo, useState } from 'react';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { BreadcrumbComponent } from '../../../Components/BreadCrumb';
import { BaseComponentImpl, RoomImpl } from '../../../types/implementations';
import { Building } from '../../../types/protocols';
import { currentDrawings } from '../../../Helpers/blueprintLookup';
import { HazardData, RiskConstructionData, WorkEnvironmentRiskData } from '../../../Helpers/utils';
import BlueprintWithMarkers from '../../../Components/BlueprintWithMarkers';
import { useRisksOverview, RisksViewMode, VIEW_MODE_LABELS, VIEW_MODE_PRIORITY } from './useRisksOverview';
import RisksOverviewList, { ListItem, RiskBadge, RiskBadgeType, RiskSectionHeader } from './RisksOverviewList';
import RisksDetailPane, { DetailContent } from './RisksDetailPane';
import { PageLoader } from '../../../Components/LoadingSpinner';

type DetailSelection =
    | { type: 'component'; componentKey: string }
    | { type: 'room'; risk: WorkEnvironmentRiskData }
    | null;

const RisksOverviewPage: React.FC = () => {
    const {
        buildingKey, currentBuilding, loadingBuilding, buildingError,
        hazards, riskConstructions, workEnvironmentRisks,
        availableModes, counts,
    } = useRisksOverview();

    // Set document title
    useDocumentTitle(
        loadingBuilding ? 'Laddar risker...' :
        buildingError ? 'Fel - Risker' :
        currentBuilding?.summary?.name ? `Risker: ${currentBuilding.summary.name}` :
        'Risker'
    );

    const [activeMode, setActiveMode] = useState<RisksViewMode>('list');
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);
    // A selected component is held by key and read from the building on every
    // render, so edits made in the detail pane show there
    const [detailSelection, setDetailSelection] = useState<DetailSelection>(null);
    const detailContent = useMemo<DetailContent>(() => {
        if (detailSelection?.type !== 'component') return detailSelection;
        const data = currentBuilding?.model.structure?.components?.[detailSelection.componentKey];
        return data ? { type: 'component', component: new BaseComponentImpl(data) } : null;
    }, [detailSelection, currentBuilding]);

    // Set default mode when available modes change. Keyed on the modes, not the
    // array, which is rebuilt on every building update (an edit in the pane)
    const availableModesKey = availableModes.join(',');
    useEffect(() => {
        const defaultMode = VIEW_MODE_PRIORITY.find(m => availableModesKey.split(',').includes(m)) ?? 'list';
        setActiveMode(defaultMode);
        setSelectedKey(null);
        setDetailSelection(null);
    }, [availableModesKey]);

    const handleModeChange = (mode: RisksViewMode) => {
        setActiveMode(mode);
        setSelectedKey(null);
        setDetailSelection(null);
        setHoveredKey(null);
    };

    const handleComponentClick = (componentKey: string) => {
        if (!currentBuilding?.model.structure?.components?.[componentKey]) return;
        const isDeselecting = selectedKey === componentKey;
        if (isDeselecting) {
            setSelectedKey(null);
            setDetailSelection(null);
        } else {
            setSelectedKey(componentKey);
            setDetailSelection({ type: 'component', componentKey });
        }
    };

    const handleRoomClick = (roomKey: string) => {
        const isDeselecting = selectedKey === roomKey;
        if (isDeselecting) {
            setSelectedKey(null);
            setDetailSelection(null);
        } else {
            setSelectedKey(roomKey);
            const risk = workEnvironmentRisks.find(w => w.roomKey === roomKey);
            if (risk) {
                setDetailSelection({ type: 'room', risk });
            }
        }
    };

    // Collect all risk-bearing components as BaseComponentImpl for blueprint/map views
    const riskComponents = useMemo(() => {
        if (!currentBuilding?.model.structure?.components) return [];
        const components = currentBuilding.model.structure.components;
        const keys = new Set<string>();
        hazards.forEach(h => h.components.forEach(c => keys.add(c.key)));
        riskConstructions.forEach(r => r.components.forEach(c => keys.add(c.key)));
        return Array.from(keys)
            .filter(key => components[key])
            .map(key => new BaseComponentImpl(components[key]));
    }, [currentBuilding, hazards, riskConstructions]);

    // Collect rooms with work environment risks as RoomImpl for blueprint views
    const riskRooms = useMemo(() => {
        if (!currentBuilding?.model.structure?.rooms) return [];
        const rooms = currentBuilding.model.structure.rooms;
        return workEnvironmentRisks
            .filter(w => rooms[w.roomKey])
            .map(w => new RoomImpl(rooms[w.roomKey]));
    }, [currentBuilding, workEnvironmentRisks]);

    // Build lookup: component/room key → risk badge type
    const riskTypeByKey = useMemo(() => {
        const map = new Map<string, RiskBadgeType>();
        hazards.forEach(h => h.components.forEach(c => map.set(c.key, 'hazard')));
        riskConstructions.forEach(r => r.components.forEach(c => {
            if (!map.has(c.key)) map.set(c.key, 'riskConstruction');
        }));
        workEnvironmentRisks.forEach(w => map.set(w.roomKey, 'workEnvironment'));
        return map;
    }, [hazards, riskConstructions, workEnvironmentRisks]);

    if (loadingBuilding) return <PageLoader />;
    if (buildingError) return <p style={{ color: 'red' }}>{buildingError}</p>;
    if (!buildingKey) return <p>Missing building key</p>;
    if (!currentBuilding) return <p>Building data missing</p>;

    const { summary } = currentBuilding;
    const showTabs = availableModes.length > 1;

    const renderActiveView = () => {
        if (counts.total === 0) {
            return (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999', border: '2px dashed #ddd', borderRadius: '8px' }}>
                    <p style={{ margin: 0 }}>Inga risker registrerade</p>
                </div>
            );
        }

        switch (activeMode) {
            case 'list':
                return (
                    <RisksOverviewList
                        hazards={hazards}
                        riskConstructions={riskConstructions}
                        workEnvironmentRisks={workEnvironmentRisks}
                        selectedKey={selectedKey}
                        onComponentClick={handleComponentClick}
                        onRoomClick={handleRoomClick}
                    />
                );
            case 'blueprint':
                return (
                    <RisksBlueprintView
                        components={riskComponents}
                        rooms={riskRooms}
                        building={currentBuilding}
                        buildingKey={buildingKey}
                        selectedKey={selectedKey}
                        hoveredKey={hoveredKey}
                        riskTypeByKey={riskTypeByKey}
                        hazards={hazards}
                        riskConstructions={riskConstructions}
                        workEnvironmentRisks={workEnvironmentRisks}
                        onComponentClick={handleComponentClick}
                        onRoomClick={handleRoomClick}
                        onHover={setHoveredKey}
                    />
                );
            case 'map':
                return (
                    <RisksMapView
                        components={riskComponents}
                        rooms={riskRooms}
                        hazards={hazards}
                        riskConstructions={riskConstructions}
                        workEnvironmentRisks={workEnvironmentRisks}
                        building={currentBuilding}
                        buildingKey={buildingKey}
                        selectedKey={selectedKey}
                        hoveredKey={hoveredKey}
                        riskTypeByKey={riskTypeByKey}
                        onComponentClick={handleComponentClick}
                        onRoomClick={handleRoomClick}
                        onHover={setHoveredKey}
                    />
                );
        }
    };

    return (
        <>
            <BreadcrumbComponent
                items={[
                    { text: summary.name ?? 'Byggnad', link: `/building/${buildingKey}` },
                    { text: 'Risker' },
                ]}
            />
            <div style={{
                marginLeft: '20px',
                paddingTop: '40px',
                paddingRight: '40px',
                paddingBottom: '40px',
                paddingLeft: 0,
                maxWidth: '1500px',
            }}>
                {/* Tab bar */}
                {showTabs && (
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
                        {availableModes.map(mode => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => handleModeChange(mode)}
                                style={{
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    border: activeMode === mode
                                        ? '1px solid rgba(54, 158, 195, 0.3)'
                                        : '1px solid transparent',
                                    backgroundColor: activeMode === mode
                                        ? 'rgba(54, 158, 195, 0.1)'
                                        : 'transparent',
                                    color: activeMode === mode ? '#369ec3' : '#6c757d',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {VIEW_MODE_LABELS[mode]}
                            </button>
                        ))}
                    </div>
                )}

                {/* Two-pane layout */}
                <div style={{ display: 'flex', gap: '40px' }}>
                    {/* Left: active view */}
                    <div style={{ flex: '1', minWidth: 0 }}>
                        {renderActiveView()}
                    </div>

                    {/* Right: detail pane */}
                    <div style={{ width: '400px', flexShrink: 0 }}>
                        <RisksDetailPane
                            content={detailContent}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

// ── Blueprint View ──────────────────────────────────────────────

interface RisksBlueprintViewProps {
    components: BaseComponentImpl[];
    rooms: RoomImpl[];
    building: Building;
    buildingKey: string;
    selectedKey: string | null;
    hoveredKey: string | null;
    riskTypeByKey: Map<string, RiskBadgeType>;
    hazards: HazardData[];
    riskConstructions: RiskConstructionData[];
    workEnvironmentRisks: WorkEnvironmentRiskData[];
    onComponentClick: (key: string) => void;
    onRoomClick: (roomKey: string) => void;
    onHover: (key: string | null) => void;
}

const RisksBlueprintView: React.FC<RisksBlueprintViewProps> = ({
    components,
    rooms,
    building,
    buildingKey,
    selectedKey,
    hoveredKey,
    riskTypeByKey,
    hazards,
    riskConstructions,
    workEnvironmentRisks,
    onComponentClick,
    onRoomClick,
    onHover,
}) => {
    const { drawingsWithData, unlocatedComponents, unlocatedRooms } = useMemo(() => {
        // Every current drawing: levels and the roof drawing ("Takplan")
        const drawings = currentDrawings(building.model.structure);
        if (drawings.length === 0) {
            return { drawingsWithData: [], unlocatedComponents: components, unlocatedRooms: rooms };
        }
        const byImageKey = new Map(drawings.map(drawing => [drawing.imageKey, drawing]));

        // Partition components
        const drawingComponentsMap = new Map<string, BaseComponentImpl[]>();
        const drawingRoomsMap = new Map<string, RoomImpl[]>();
        const unlocComp: BaseComponentImpl[] = [];
        const unlocRoom: RoomImpl[] = [];

        components.forEach(component => {
            if (!component.blueprintLocations) {
                unlocComp.push(component);
                return;
            }
            let placed = false;
            for (const bpKey of Object.keys(component.blueprintLocations)) {
                if (byImageKey.has(bpKey)) {
                    const existing = drawingComponentsMap.get(bpKey) ?? [];
                    existing.push(component);
                    drawingComponentsMap.set(bpKey, existing);
                    placed = true;
                    break;
                }
            }
            if (!placed) unlocComp.push(component);
        });

        rooms.forEach(room => {
            if (!room.blueprintLocations) {
                unlocRoom.push(room);
                return;
            }
            let placed = false;
            for (const bpKey of Object.keys(room.blueprintLocations)) {
                if (byImageKey.has(bpKey)) {
                    const existing = drawingRoomsMap.get(bpKey) ?? [];
                    existing.push(room);
                    drawingRoomsMap.set(bpKey, existing);
                    placed = true;
                    break;
                }
            }
            if (!placed) unlocRoom.push(room);
        });

        // Only drawings that have at least one component or room; highest level first, roof drawing last
        const result = drawings
            .filter(drawing => drawingComponentsMap.has(drawing.imageKey) || drawingRoomsMap.has(drawing.imageKey))
            .map(drawing => ({
                drawing,
                components: drawingComponentsMap.get(drawing.imageKey) ?? [],
                rooms: drawingRoomsMap.get(drawing.imageKey) ?? [],
            }))
            .sort((a, b) => {
                if (a.drawing.owner.kind !== b.drawing.owner.kind) return a.drawing.owner.kind === 'roofPlan' ? 1 : -1;
                return b.drawing.order - a.drawing.order;
            });

        return { drawingsWithData: result, unlocatedComponents: unlocComp, unlocatedRooms: unlocRoom };
    }, [components, rooms, building]);

    if (drawingsWithData.length === 0 && unlocatedComponents.length === 0 && unlocatedRooms.length === 0) {
        return <p>Inga ritningar tillgängliga</p>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {drawingsWithData.map(({ drawing, components: drawingComps, rooms: drawingRms }) => (
                <BlueprintWithMarkers
                    key={drawing.imageKey}
                    blueprint={drawing.image}
                    currentBlueprintKey={drawing.imageKey}
                    building={building}
                    utilities={drawingComps}
                    rooms={drawingRms}
                    onUtilityClick={onComponentClick}
                    onRoomClick={onRoomClick}
                    title={drawing.label}
                />
            ))}

            {(unlocatedComponents.length > 0 || unlocatedRooms.length > 0) && (
                <UnlocatedRisksList
                    components={unlocatedComponents}
                    rooms={unlocatedRooms}
                    building={building}
                    riskTypeByKey={riskTypeByKey}
                    hazards={hazards}
                    riskConstructions={riskConstructions}
                    workEnvironmentRisks={workEnvironmentRisks}
                    selectedKey={selectedKey}
                    onComponentClick={onComponentClick}
                    onRoomClick={onRoomClick}
                />
            )}
        </div>
    );
};

// ── Map View ────────────────────────────────────────────────────

interface RisksMapViewProps {
    components: BaseComponentImpl[];
    rooms: RoomImpl[];
    hazards: HazardData[];
    riskConstructions: RiskConstructionData[];
    workEnvironmentRisks: WorkEnvironmentRiskData[];
    building: Building;
    buildingKey: string;
    selectedKey: string | null;
    hoveredKey: string | null;
    riskTypeByKey: Map<string, RiskBadgeType>;
    onComponentClick: (key: string) => void;
    onRoomClick: (roomKey: string) => void;
    onHover: (key: string | null) => void;
}

const RisksMapView: React.FC<RisksMapViewProps> = ({
    components,
    rooms,
    hazards,
    riskConstructions,
    workEnvironmentRisks,
    building,
    buildingKey,
    selectedKey,
    hoveredKey,
    riskTypeByKey,
    onComponentClick,
    onRoomClick,
    onHover,
}) => {
    const mapImageKey = building.model.currentMapImageKey;
    const mapImage = mapImageKey ? building.model.mapImages?.[mapImageKey] : undefined;

    const { wrappedComponents, unlocatedComponents } = useMemo(() => {
        if (!mapImageKey) {
            return { wrappedComponents: [], unlocatedComponents: components };
        }

        const wrapped: BaseComponentImpl[] = [];
        const unlocated: BaseComponentImpl[] = [];

        components.forEach(component => {
            if (component.mapLocations && component.mapLocations[mapImageKey]) {
                const w = Object.create(component) as BaseComponentImpl;
                w.blueprintLocations = {
                    [mapImageKey]: component.mapLocations[mapImageKey],
                };
                wrapped.push(w);
            } else {
                unlocated.push(component);
            }
        });

        return { wrappedComponents: wrapped, unlocatedComponents: unlocated };
    }, [components, mapImageKey]);

    if (!mapImage || !mapImageKey) {
        return (
            <div>
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#999',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                }}>
                    <p style={{ margin: 0 }}>Ingen kartbild tillgänglig</p>
                </div>

                {(components.length > 0 || rooms.length > 0) && (
                    <UnlocatedRisksList
                        components={components}
                        rooms={rooms}
                        building={building}
                        riskTypeByKey={riskTypeByKey}
                        hazards={hazards}
                        riskConstructions={riskConstructions}
                        workEnvironmentRisks={workEnvironmentRisks}
                        selectedKey={selectedKey}
                        onComponentClick={onComponentClick}
                        onRoomClick={onRoomClick}
                        style={{ marginTop: '24px' }}
                    />
                )}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <BlueprintWithMarkers
                blueprint={mapImage}
                currentBlueprintKey={mapImageKey}
                building={building}
                utilities={wrappedComponents}
                onUtilityClick={onComponentClick}
                title="Karta"
            />

            {(unlocatedComponents.length > 0 || rooms.length > 0) && (
                <UnlocatedRisksList
                    components={unlocatedComponents}
                    rooms={rooms}
                    building={building}
                    riskTypeByKey={riskTypeByKey}
                    hazards={hazards}
                    riskConstructions={riskConstructions}
                    workEnvironmentRisks={workEnvironmentRisks}
                    selectedKey={selectedKey}
                    onComponentClick={onComponentClick}
                    onRoomClick={onRoomClick}
                />
            )}
        </div>
    );
};

// ── Categorized Unlocated Risks List ─────────────────────────────

const RISK_CATEGORY_ORDER: { type: RiskBadgeType; title: string }[] = [
    { type: 'hazard', title: 'Miljö/hälsofara' },
    { type: 'riskConstruction', title: 'Riskkonstruktioner' },
    { type: 'workEnvironment', title: 'Arbetsmiljörisker' },
];

const UnlocatedRisksList: React.FC<{
    components: BaseComponentImpl[];
    rooms: RoomImpl[];
    building: Building;
    riskTypeByKey: Map<string, RiskBadgeType>;
    hazards: HazardData[];
    riskConstructions: RiskConstructionData[];
    workEnvironmentRisks: WorkEnvironmentRiskData[];
    selectedKey: string | null;
    onComponentClick: (key: string) => void;
    onRoomClick: (roomKey: string) => void;
    style?: React.CSSProperties;
}> = ({ components, rooms, building, riskTypeByKey, hazards, riskConstructions, workEnvironmentRisks, selectedKey, onComponentClick, onRoomClick, style }) => {
    // Get set of unlocated component keys for counting
    const unlocatedComponentKeys = useMemo(() => new Set(components.map(c => c.key)), [components]);
    // Build subLabel lookups
    const componentSubLabels = useMemo(() => {
        const map = new Map<string, string>();

        // Hazards: group by component key
        const hazardDescriptions = new Map<string, string[]>();
        hazards.forEach(hazardData => {
            hazardData.components.forEach(component => {
                if (!hazardDescriptions.has(component.key)) {
                    hazardDescriptions.set(component.key, []);
                }
                const desc = `${hazardData.hazardType}${component.hazardState ? ` — ${component.hazardState}` : ''}`;
                hazardDescriptions.get(component.key)!.push(desc);
            });
        });
        hazardDescriptions.forEach((descriptions, key) => {
            map.set(key, descriptions.join(', '));
        });

        // Risk constructions: group by component key
        const riskTypes = new Map<string, string[]>();
        riskConstructions.forEach(riskData => {
            riskData.components.forEach(component => {
                if (!riskTypes.has(component.key)) {
                    riskTypes.set(component.key, []);
                }
                riskTypes.get(component.key)!.push(riskData.riskType);
            });
        });
        riskTypes.forEach((types, key) => {
            if (!map.has(key)) {
                map.set(key, types.join(', '));
            }
        });

        return map;
    }, [hazards, riskConstructions]);

    const roomSubLabels = useMemo(() => {
        const map = new Map<string, string>();
        workEnvironmentRisks.forEach(risk => {
            const preview = risk.description.length > 80
                ? risk.description.substring(0, 80) + '...'
                : risk.description;
            map.set(risk.roomKey, preview);
        });
        return map;
    }, [workEnvironmentRisks]);

    // Group components by risk type
    const grouped = useMemo(() => {
        const map = new Map<RiskBadgeType, { components: BaseComponentImpl[]; rooms: RoomImpl[] }>();
        components.forEach(c => {
            const type = riskTypeByKey.get(c.key) ?? 'hazard';
            const group = map.get(type) ?? { components: [], rooms: [] };
            group.components.push(c);
            map.set(type, group);
        });
        rooms.forEach(r => {
            const group = map.get('workEnvironment') ?? { components: [], rooms: [] };
            group.rooms.push(r);
            map.set('workEnvironment', group);
        });
        return map;
    }, [components, rooms, riskTypeByKey]);

    // Count unique risk types (not components) for each category
    const riskTypeCounts = useMemo(() => {
        // Count hazard types that have at least one unlocated component
        const hazardCount = hazards.filter(h =>
            h.components.some(c => unlocatedComponentKeys.has(c.key))
        ).length;

        // Count risk construction types that have at least one unlocated component
        const riskConstructionCount = riskConstructions.filter(r =>
            r.components.some(c => unlocatedComponentKeys.has(c.key))
        ).length;

        // Work environment risks are already counted per room
        const workEnvCount = rooms.length;

        return {
            hazard: hazardCount,
            riskConstruction: riskConstructionCount,
            workEnvironment: workEnvCount,
        };
    }, [hazards, riskConstructions, rooms, unlocatedComponentKeys]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', ...style }}>
            {RISK_CATEGORY_ORDER.map(({ type, title }) => {
                const group = grouped.get(type);
                if (!group) return null;
                // Use risk type count (not component count) for the header
                const count = riskTypeCounts[type];

                return (
                    <div key={type}>
                        <RiskSectionHeader title={title} count={count} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                            {group.components.map(c => (
                                <ListItem
                                    key={c.key}
                                    label={c.displayName(building)}
                                    subLabel={componentSubLabels.get(c.key)}
                                    badgeType={type}
                                    isSelected={selectedKey === c.key}
                                    onClick={() => onComponentClick(c.key)}
                                />
                            ))}
                            {group.rooms.map(r => (
                                <ListItem
                                    key={r.key}
                                    label={r.displayName()}
                                    subLabel={roomSubLabels.get(r.key)}
                                    badgeType={type}
                                    isSelected={selectedKey === r.key}
                                    onClick={() => onRoomClick(r.key)}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default RisksOverviewPage;
